import { DynamicStandaloneAdunit } from '@/components/Adunit';
import { DynamicStandaloneSpinner } from '@/components/Spinner';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { ErrorNode } from 'base/components/ErrorBoundary';
import { getAdUnit, shouldInjectAdAtRow } from 'lib/data/compute/ads/helpers';
import { getDomain } from 'lib/utils';
import getUrl from 'lib/utils/getUrl';
import logger from 'lib/utils/logger';
import { Fragment, ReactNode, useCallback, useMemo, useState } from 'react';
import { Search, SearchProps } from './Search';
import { SearchItemProps } from './Search.Item';

interface SearchResult {
  date: string;
  image: string;
  link: string;
  snippet: string;
  title: string;
}

export const fetchSearchResults = async (query: string, next?: number, filter?: string) => {
  const host = getDomain();
  const url = getUrl(`/api/search`, host);

  if (!url) {
    logger.error('Search: Could not construct URL');
    return {};
  }

  url?.searchParams.set('term', query);
  url?.searchParams.set('start', `${next ?? 1}`);
  url?.searchParams.set('filter', filter ?? '');

  try {
    const response = await fetch(url.href);
    if (response.status !== 200) {
      logger.error('Search: API returned ' + response.status);
      return {};
    }
    return response.json();
  } catch {
    logger.catch('Search: There was an unknown error.');
    return {};
  }
};

export interface StandaloneSearchProps extends StandaloneComponentProps {
  initial?: {
    results?: SearchResult[];
    next?: number;
  };
  query?: string;
  adsUniqueId?: string;
  total?: number;
  pageType?: 'authorPage' | 'searchResults';
  headline?: ReactNode;
  button?: ReactNode;
  options?: SearchProps & {
    $item?: SearchItemProps;
  };
}

export const StandaloneSearch: StandaloneComponent<StandaloneSearchProps> = ({
  initial,
  query,
  adsUniqueId = '',
  total,
  headline,
  button,
  options,
  pageType,
  ...props
}) => {
  const [results, setResults] = useState<SearchResult[]>(initial?.results ?? []);
  const [next, setNext] = useState(initial?.next || 1);
  const [isError, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const { $item: itemProps, ...baseProps } = options ?? {};

  const onLoadMore = useCallback(async () => {
    if (isLoading) return;

    if (!query || !next) {
      setNext(0);
      return logger.error(`Load more search results have failed.`, { query, next });
    }

    setLoading(true);

    const resultsFilter = pageType === 'authorPage' ? 'more:pagemap:metatags-og_type:article' : '';
    const response = await fetchSearchResults(query, next, resultsFilter).catch((error) => {
      logger.catch(error);
      return setError(true);
    });

    if (!response || response.status !== 'search_successful') {
      logger.error(response);
      return setError(true);
    }

    setNext(response.nextPage || 0);

    setResults([...results, ...response.items]);

    setLoading(false);
  }, [isLoading, next, query, results]);

  const defaultHeadline: SearchProps['headline'] = useMemo(() => {
    if (pageType === 'authorPage') {
      return (
        <>
          Artiklar av <em>{query || '?'}</em>
        </>
      );
    }

    return (
      <>
        Sökresultat för <em>{query || '?'}</em>
      </>
    );
  }, [query, pageType]);

  let adPlacementNo = 1;

  const caption: SearchProps['caption'] = `Hittade ${total || 0} resultat`;

  const content: SearchProps['content'] = (
    <>
      {results?.map((item, index) => (
        <Fragment key={index}>
          <Search.Item
            headline={item.title}
            caption={item.date}
            description={item.snippet}
            image={{ src: item.image, alt: item.title }}
            links={{ article: { href: item.link } }}
          />
          {shouldInjectAdAtRow({ currentRow: index, frequency: 3 }) && (
            <DynamicStandaloneAdunit
              data={getAdUnit('responsive', adPlacementNo++, adsUniqueId)?.data}
              title={'Annons'}
            />
          )}
        </Fragment>
      ))}
      {isLoading && <DynamicStandaloneSpinner />}
    </>
  );

  if (isError) {
    return ErrorNode;
  }

  const isButtonDisabled = !next || isLoading || isError;

  return (
    <Search
      button={{ content: button ?? 'Ladda mer' }}
      options={{ $button: { disabled: isButtonDisabled, onClick: onLoadMore } }}
      headline={headline || defaultHeadline}
      {...{ caption, content }}
      {...baseProps}
      {...props}
    />
  );
};

export default StandaloneSearch;
